import type { ChartRuleErrorCode, ChartRuleResolveError, CustomChartType } from './types';
import { CUSTOM_CHART_TYPES } from './constants';

/** Коды отказа, которые бэкенд отдаёт по правилу отбора данных диаграммы. */
const CHART_RULE_ERROR_CODES: readonly ChartRuleErrorCode[] = [
  'description_not_understood',
  'data_not_available',
  'chart_type_mismatch',
  'query_rejected',
  'query_failed',
];

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const isChartRuleErrorCode = (value: unknown): value is ChartRuleErrorCode =>
  typeof value === 'string' && (CHART_RULE_ERROR_CODES as readonly string[]).includes(value);

const toSuggestedChartTypes = (value: unknown): CustomChartType[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const types = value.filter(
    (item): item is CustomChartType =>
      typeof item === 'string' && (CUSTOM_CHART_TYPES as readonly string[]).includes(item),
  );
  return types.length > 0 ? types : undefined;
};

/**
 * Выделяет отказ по правилу диаграммы из ошибки запроса.
 *
 * Тело такого отказа — `{code, message}`, и `message` написан для показа пользователю: его
 * ставят в форму диаграммы или в сам блок. Ошибка другой природы (сеть, 500, отказ валидации
 * тела запроса) сообщения для пользователя не несёт — на неё возвращается `null`, и вызывающий
 * показывает свой общий текст.
 */
export const asChartRuleError = (error: unknown): ChartRuleResolveError | null => {
  if (!isRecord(error)) return null;
  const body = isRecord(error.data) ? error.data : null;
  if (!body) return null;
  if (!isChartRuleErrorCode(body.code) || typeof body.message !== 'string') return null;
  return {
    code: body.code,
    message: body.message,
    suggestedChartTypes: toSuggestedChartTypes(body.suggestedChartTypes),
  };
};
